import { GbpPhotosBulkUploadView, GrowthDashboardTopBarActions } from '@elva/ui';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AsyncBoundary } from '@/components/feedback';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { viewProps } from '@/data/viewProps';
import { syncedCategoriesFromSlots } from '@/features/gbp-photos/api';
import {
  useGbpPhotoMutations,
  useGbpUploadSession,
} from '@/features/gbp-photos/hooks/useGbpPhotos';

type GbpPhotosBulkUploadLocationState = {
  promptId?: string;
  openFilePicker?: boolean;
};

export function GbpPhotosBulkUploadPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as GbpPhotosBulkUploadLocationState;
  const [sessionId, setSessionId] = useState<string | undefined>();

  usePageTopBar({
    actions: <GrowthDashboardTopBarActions />,
  });

  const { uploadPhotos, syncSlots, removeSlot, cancelSession } = useGbpPhotoMutations();
  const { data: sessionReview, isFetching: pollingSession } = useGbpUploadSession(
    sessionId ?? '',
    Boolean(sessionId),
  );

  const syncedCategories = useMemo(
    () => (sessionReview?.slots ? syncedCategoriesFromSlots(sessionReview.slots) : []),
    [sessionReview?.slots],
  );

  return (
    <AsyncBoundary loading={false}>
      <GbpPhotosBulkUploadView
        {...viewProps({
          loading: uploadPhotos.isPending,
          syncing: syncSlots.isPending,
          openFilePickerOnMount: Boolean(state.openFilePicker),
          processingProgress: sessionReview
            ? {
                total: sessionReview.photosTotal,
                processed: sessionReview.photosProcessed,
              }
            : undefined,
          processingComplete: sessionReview?.isReadyForReview,
          reviewPhotos: sessionReview?.slots,
          reviewSummary: sessionReview?.summary,
          syncedCategories: syncedCategories.length ? syncedCategories : undefined,
          polling: pollingSession,
          onUpload: async (files: File[]) => {
            const id = await uploadPhotos.mutateAsync({
              files,
              ...(state.promptId ? { promptId: state.promptId } : {}),
            });
            setSessionId(id);
          },
          onSync: async (slotIds: number[]) => {
            if (!sessionId) return;
            await syncSlots.mutateAsync({ sessionId, slotIds });
          },
          onRemoveSlot: (slotId: number) => {
            removeSlot.mutate(slotId);
          },
          onCancelSession: () => {
            if (sessionId) cancelSession.mutate(sessionId);
            setSessionId(undefined);
          },
          onReturn: () => navigate('/gbp/photos'),
        })}
      />
    </AsyncBoundary>
  );
}
